/**
 * sanitizeHtml - Safe HTML rendering for novel content
 * Location: /app/frontend/src/components/page-builder/reader/hooks/
 * 
 * Allows only safe HTML tags to prevent XSS attacks.
 */

// Allowed HTML tags for novel content
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'span', 'div',
  'hr',
]);

// Allowed attributes per tag
const ALLOWED_ATTRS = {
  'a': ['href', 'title', 'target', 'rel'],
  'img': ['src', 'alt', 'width', 'height'],
  'span': ['class'],
  'div': ['class'],
  'p': ['class'],
};

// URL schemes allowed in href
const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:'];

/**
 * Check if URL is safe
 */
const isSafeUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return ALLOWED_SCHEMES.some(scheme => parsed.protocol === scheme);
  } catch {
    // Relative URLs are safe
    return !url.includes('javascript:') && !url.includes('data:');
  }
};

/**
 * Sanitize HTML string - removes dangerous elements and attributes
 * @param {string} html - Raw HTML string
 * @returns {string} Sanitized HTML string
 */
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary container
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Recursively clean nodes
  const cleanNode = (node) => {
    const children = Array.from(node.childNodes);
    
    children.forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toLowerCase();
        
        // Remove disallowed tags
        if (!ALLOWED_TAGS.has(tagName)) {
          // Keep text content but remove the tag
          const textContent = child.textContent;
          const textNode = document.createTextNode(textContent);
          node.replaceChild(textNode, child);
          return;
        }
        
        // Remove disallowed attributes
        const allowedAttrs = ALLOWED_ATTRS[tagName] || [];
        const attrs = Array.from(child.attributes);
        attrs.forEach(attr => {
          if (!allowedAttrs.includes(attr.name)) {
            child.removeAttribute(attr.name);
          }
          
          // Validate href attributes
          if (attr.name === 'href' && !isSafeUrl(attr.value)) {
            child.removeAttribute('href');
          }
        });
        
        // Add safety attributes to links
        if (tagName === 'a') {
          child.setAttribute('rel', 'noopener noreferrer');
          if (!child.getAttribute('target')) {
            child.setAttribute('target', '_blank');
          }
        }
        
        // Recursively clean children
        cleanNode(child);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        // Remove comments
        node.removeChild(child);
      }
    });
  };
  
  cleanNode(temp);
  return temp.innerHTML;
};

/**
 * Convert plain text with line breaks to HTML paragraphs
 */
export const textToHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs
    .map(p => {
      // Convert single newlines to <br>
      const withBreaks = p.trim().replace(/\n/g, '<br>');
      return withBreaks ? `<p>${withBreaks}</p>` : '';
    })
    .filter(Boolean)
    .join('\n');
};

/**
 * Remove all HTML tags, keeping only text
 */
export const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

export default sanitizeHtml;
